import type { IconName } from "@similarweb/icons";

import { translate } from "app/filters/i18nFilter";
import { minVisitsAbbrFilter, timeFilter, percentageSignFilter } from "app/filters/ngFilters";
import { numberFilter } from "app/filters/numberFilter";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import type { ChartDataPoint, HighLevelMetricsComparisonChartData, MetricKey, TimeGranularity } from "../types";

type FormatterFunction = (value: number) => string;

type MetricConfiguration = {
    metricName: string;
    iconName: IconName;
    dataPath: (data: HighLevelMetricsComparisonChartData, timeGranularity: TimeGranularity) => ChartDataPoint[];
    yAxisFormatter: FormatterFunction;
    tooltipFormatter: FormatterFunction;
    supportedGranularities: TimeGranularity[];
};

export const METRIC_CONFIGURATION: Record<MetricKey, MetricConfiguration> = {
    "monthly-visits": {
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.monthlyVisits");
        },
        iconName: "month",
        dataPath: (data, timeGranularity) => data.Visits[timeGranularity].Graph,
        yAxisFormatter: minVisitsAbbrFilter(),
        tooltipFormatter: minVisitsAbbrFilter(),
        supportedGranularities: [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY],
    },
    "unique-visitors": {
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.uniqueVisitors");
        },
        iconName: "monthly-unique-visitors",
        dataPath: (data, timeGranularity) => {
            // UniqueUsers only has Monthly data based on the type definition
            if (timeGranularity === GranularityNames.MONTHLY) {
                return data.UniqueUsers?.Monthly?.Graph || [];
            }
            return [];
        },
        yAxisFormatter: minVisitsAbbrFilter(),
        tooltipFormatter: minVisitsAbbrFilter(),
        supportedGranularities: [GranularityNames.MONTHLY],
    },
    "visit-duration": {
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.visitDuration");
        },
        iconName: "avg-visit-duration",
        dataPath: (data, timeGranularity) => data.VisitDuration[timeGranularity].Graph,
        yAxisFormatter: (value: number) => timeFilter()(value, null) as string,
        tooltipFormatter: (value: number) => timeFilter()(value, null) as string,
        supportedGranularities: [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY],
    },
    "pages-per-visit": {
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.pagesPerVisit");
        },
        iconName: "pages-per-visit",
        dataPath: (data, timeGranularity) => data.PagesPerVisit[timeGranularity].Graph,
        yAxisFormatter: (value: number) => numberFilter()(value, 2),
        tooltipFormatter: (value: number) => numberFilter()(value, 2),
        supportedGranularities: [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY],
    },
    "bounce-rate": {
        get metricName() {
            return translate("market.research.overview.comparison.highlevel.tabs.bounceRate");
        },
        iconName: "bounce-rate-2",
        dataPath: (data, timeGranularity) => data.BounceRate[timeGranularity].Graph,
        yAxisFormatter: (value: number) => percentageSignFilter()(value, 2) as string,
        tooltipFormatter: (value: number) => percentageSignFilter()(value, 2) as string,
        supportedGranularities: [GranularityNames.DAILY, GranularityNames.WEEKLY, GranularityNames.MONTHLY],
    },
};

export const METRIC_KEYS = Object.keys(METRIC_CONFIGURATION) as MetricKey[];

// Helper functions for easier access
const getMetricConfig = (metric: MetricKey) => METRIC_CONFIGURATION[metric];

export const isMetricSupportedForGranularity = (
    selectedMetric: MetricKey,
    timeGranularity: TimeGranularity,
): boolean => {
    const config = METRIC_CONFIGURATION[selectedMetric];
    return config?.supportedGranularities.includes(timeGranularity) || false;
};
